/**
 * The usage platform — widgets, the events they meter, the caps that bound the bill
 * and the prepaid balance that pays for them when there is no retainer.
 *
 * `ARCHITECTURE.md` §Widgets and usage platform: "This is the part that has to scale
 * in *kinds*, not just in volume. The portal owns the registry, the keys, the
 * metering, the caps and the billing. Widgets themselves are separate services."
 * Everything here is the **wire** shape of that: snake_case, refs rather than ids,
 * money as `{ amount, currency }` in minor units.
 *
 * Four things are worth reading before the rest.
 *
 *   **`UsageStateSchema` is the one schema two packages share.**
 *   `.docs/13-usage-platform.md` §DRY watchlist: "The SDK's state schema is
 *   `packages/shared/schemas/usage.ts`, shared with the route." The ingestion route
 *   answers with it and `@fareast/widget-sdk` reads it — as a **type**, because
 *   `eslint.config.js` allows the SDK `import type` from this package and nothing
 *   else, and because the SDK is fail-open and has nothing useful to do with a
 *   validation failure it cannot fix.
 *
 *   **`used` is everything recorded; `billable` is what will be charged.** They are
 *   different numbers and both widget pages print both: "1,264" against "264 over
 *   the allowance, and billable". Events past the cap are recorded and never billed
 *   (`.docs/00-index.md` §6), so `used` can exceed `cap` and `billable` cannot.
 *
 *   **There is no `mode` field on a widget.** `SERVICES.md` §Usage: "Usage mode is
 *   derived: a client with an active retainer is postpay; otherwise prepay. It is
 *   not a setting." `WidgetSummarySchema.mode` is the derivation's answer at the
 *   moment it was asked, which is what `client/widget-chatbot.html` prints as
 *   "Billing mode · Postpay — you hold an active retainer".
 *
 *   **Every amount on a widget is in the client's billing currency.** The rate card
 *   is USD, but a widget's `unit_amount` is snapshotted at creation and every event
 *   copies it again at record time, so nothing here needs a tier or a conversion —
 *   `pricing.usageUnitPrice` did both once, when the widget was created.
 */
import { z } from 'zod';
import { widgetMachine } from '../states.js';
import { CatalogCodeSchema, HostedSurfaceSchema } from './catalog.js';
import { DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema } from './primitives.js';
import { ClientSlugSchema, WidgetRefSchema } from './refs.js';
/* ---------------------------------------------------------------- the words */
/**
 * What the ingestion API answers, and what the widget shows because of it.
 *
 * `ARCHITECTURE.md` §Ingestion API: `"state": "ok" | "limit_reached" | "paused"`.
 * Not a state machine and deliberately not one — `widgetMachine` has two states and
 * `limit_reached` is not one of them (`packages/shared/src/states.ts`). This is what
 * the portal *answers*, computed from the month and the ledger on every call.
 */
export const USAGE_STATES = ['ok', 'limit_reached', 'paused'];
export const UsageStateNameSchema = z
    .enum(USAGE_STATES)
    .describe('What the widget should do now. "ok" — serve. "limit_reached" — the monthly cap is ' +
    'reached; show the fallback, and nothing past the cap is billed. "paused" — the ' +
    'widget was paused in the portal, or the client is prepaid and out of balance.');
/** Whether the event was written now or had already been written. */
export const USAGE_RECORD_STATUSES = ['recorded', 'duplicate'];
export const UsageRecordStatusSchema = z
    .enum(USAGE_RECORD_STATUSES)
    .describe('"recorded" — this event is new. "duplicate" — an event with that key was already ' +
    'recorded and nothing was written; the state is the one it was recorded with. ' +
    'Either way the call succeeded and the widget should carry on.');
/**
 * How a client pays for usage. Derived from whether they hold a live retainer, never
 * stored (`SERVICES.md` §Usage, `ARCHITECTURE.md` §Billing modes).
 */
export const BILLING_MODES = ['postpay', 'prepay'];
export const BillingModeSchema = z
    .enum(BILLING_MODES)
    .describe('"postpay" — the client holds a live retainer, so usage rides the monthly invoice in ' +
    'arrears. "prepay" — no retainer, so each event debits a prepaid balance. Derived ' +
    'from the retainer, never a setting.');
/** What a credit ledger row is. Append-only; a correction is another row. */
export const CREDIT_LEDGER_KINDS = ['topup', 'usage', 'refund', 'adjustment'];
export const CreditLedgerKindSchema = z
    .enum(CREDIT_LEDGER_KINDS)
    .describe('"topup" — money added, by Checkout or auto top-up. "usage" — a widget event debited ' +
    'it. "refund" — money returned. "adjustment" — a staff correction, signed, with a ' +
    'note. The ledger is append-only; nothing is ever edited.');
/**
 * How a month of a widget's monthly fee was closed by the billing run (session 46c).
 *
 * "billed" — it became a `widget_fee` line on that run's invoice. "paused" — the widget
 * was paused when the run closed the month, and pausing is the off switch: the month is
 * closed without a charge and no later run may bill it. A month is closed exactly once,
 * whichever it was (`widget_fee_month`'s unique `(widget_id, period)`).
 */
export const WIDGET_FEE_OUTCOMES = ['billed', 'paused'];
/**
 * What a client price can be the price **of** (session 46d, `.docs/46d-client-prices.md`).
 *
 * One kind today — a widget type, whose price is the four numbers a widget carries — and
 * a closed set rather than a bare column, so that the day a retainer service or a project
 * template joins the list it is a member here and a widened CHECK, not a second table.
 */
export const CLIENT_PRICE_KINDS = ['widget_type'];
/**
 * Where a widget's price came from (session 46d): **"card"** — the rate card, converted
 * into the client's currency when the widget was created or when its client price was
 * cleared; **"client"** — a price the studio set for this client, with a reason.
 */
export const WIDGET_PRICE_SOURCES = ['card', 'client'];
export const WidgetStatusSchema = z
    .enum(widgetMachine.states)
    .describe('"active" — it is serving. "paused" — it is showing its fallback, by choice.');
/* --------------------------------------------------------------- the state */
/**
 * The state block, and the only object `@fareast/widget-sdk` knows about.
 *
 * `ARCHITECTURE.md` §Ingestion API prints it verbatim: `{ "status": …, "state": …,
 * "period": …, "used": …, "included": …, "cap": … }`. The four extra fields here —
 * `widget`, `mode`, `billable` and `fallback_text` — are what a widget service
 * actually needs to render the fallback and what `client/widget-chatbot.html` prints
 * beside the count; none of them changes the shape the document gives.
 */
export const UsageStateSchema = z
    .object({
    widget: WidgetRefSchema,
    state: UsageStateNameSchema,
    period: PeriodSchema.describe('The month these numbers are for, YYYY-MM.'),
    used: z
        .int()
        .describe('Every unit recorded this month, including any past the cap. This is the number ' +
        'the client’s usage page prints as the headline.'),
    billable: z
        .int()
        .describe('What will be charged: clamp(used, included, cap) − included. Zero inside the ' +
        'allowance, and it stops rising at the cap.'),
    included: z.int().describe('Units included each month before anything is billed.'),
    cap: z
        .int()
        .describe('The monthly ceiling. At it the state is limit_reached; past it events are still ' +
        'recorded and never billed.'),
    mode: BillingModeSchema,
    balance: MoneySchema.nullable().describe('The prepaid balance left, for a prepay client. Null for a postpay one — a retainer ' +
        'client has no balance, their usage rides the invoice.'),
    fallback_text: z
        .string()
        .describe('What the site should show instead of the widget, in the client’s own words.'),
})
    .describe('What one widget stands at this month, and what it should do about it.');
/** The body of `POST /api/v1/usage/events`. */
export const RecordUsageEventSchema = z
    .strictObject({
    event_key: z
        .string()
        .min(1)
        .max(200)
        .describe('A key you generate, unique for this event for ever — a conversation id, a booking ' +
        'id. Sending it again returns the first answer and records nothing, which is what ' +
        'makes a retry safe.'),
    occurred_at: Rfc3339Schema.optional().describe('When it happened, RFC 3339 with an offset. Left out, now. More than 7 days ago or ' +
        'more than an hour in the future is refused with ClockSkew — check the machine’s clock.'),
    qty: z
        .int()
        .min(1)
        .max(1_000)
        .default(1)
        .describe('How many units this event is. Almost always 1.'),
    meta: z
        .record(z.string(), z.unknown())
        .default({})
        .describe('Anything you want kept beside the event — a page, a locale, a model name. Stored ' +
        'as JSON, never interpreted, capped at 4 KB.'),
})
    .describe('One thing a widget did, with a key that makes recording it twice harmless.');
/** What `POST /api/v1/usage/events` answers: the status, and the state block. */
export const RecordUsageEventResultSchema = UsageStateSchema.extend({
    status: UsageRecordStatusSchema,
}).describe('Whether the event was written, and what the widget stands at now.');
/* -------------------------------------------------------------- the widget */
/**
 * The three config fields, written once and spread into both bodies that carry them.
 *
 * `WidgetSchema` is what the client's page reads and `WidgetConfigSchema` is what the
 * widget's own key reads; they are different bodies for different callers and they
 * agree about this, so it is one object rather than two lists that drift.
 *
 * **The schema on a widget is the snapshot, not the type's.** A later
 * `catalog_update_widget_type!` must not make an existing widget's config stop
 * validating (`.plans/ai-product-placer/README.md` §4.1), so the widget carries its
 * own copy taken at creation, and both of these read that.
 */
const CONFIG_FIELDS = {
    config: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('The current configuration, or null when nothing has been set yet. Its keys are the ' +
        'config_schema’s properties.'),
    config_version: z
        .int()
        .nonnegative()
        .describe('Which version of the configuration this is. 0 means never configured; every ' +
        'widgets_set_config adds one, and the version is kept for ever so that a record ' +
        'naming it still resolves.'),
    config_schema: z
        .record(z.string(), z.unknown())
        .nullable()
        .describe('The JSON Schema this widget’s configuration is validated against, snapshotted from ' +
        'the widget type when the widget was created. Null when the type takes no ' +
        'configuration. Every property carries a title and an x-editor annotation.'),
};
export const WidgetSchema = z
    .object({
    ref: WidgetRefSchema,
    client: ClientSlugSchema,
    widget_type: CatalogCodeSchema.describe('The kind of widget, by catalog code.'),
    name: z.string().describe('What the client calls it — "Support chatbot".'),
    site: z.string().describe('Where it runs, as a person reads it — "casaturca.com/wholesale".'),
    status: WidgetStatusSchema,
    unit_noun: z.string().describe('One billable event, in the client’s words — "conversation".'),
    unit_amount: MoneySchema.describe('What one unit costs, in the client’s billing currency. Snapshotted when the widget ' +
        'went live and copied onto every event; a rate-card change never re-prices it, and ' +
        'only a price the studio agrees with this client does (price_source). Zero for a ' +
        'widget that is not charged per unit — one sold by the month, or a free one — which ' +
        'then has no allowance and no cap.'),
    monthly_amount: MoneySchema.describe('What the widget costs each month whatever it does, in the client’s billing currency, ' +
        'snapshotted when it was created like the unit price. Zero for a widget sold only by ' +
        'use, or a free one. Billed in arrears on the 1st, one line per month, for every ' +
        'full calendar month (UTC) it was live and was not paused when the month closed.'),
    fee_starts_in: PeriodSchema.nullable().describe('The first month the monthly fee is billed for, YYYY-MM — its first full calendar month ' +
        'live in UTC, because the first partial month is free — or null when it carries no ' +
        'fee. A month’s fee is invoiced on the 1st of the month after it.'),
    included_qty: z
        .int()
        .describe('Units included each month before anything is billed. Always 0 for a widget with no ' +
        'unit price.'),
    monthly_cap: z
        .int()
        .describe('The monthly ceiling on units. Never below included_qty, and 0 for a widget with no ' +
        'unit price, which is never limited.'),
    worst_case: MoneySchema.describe('The most this widget can bill in a month: (cap − included) × unit_amount. What the ' +
        'settings page prints as "Worst case at this cap".'),
    fallback_text: z.string().describe('What the site shows when it is not serving.'),
    pilot: z
        .boolean()
        .describe('True while the unit price is provisional rather than on the published card.'),
    price_source: z
        .enum(WIDGET_PRICE_SOURCES)
        .describe('Where the four numbers above came from: "card" — the rate card, in the client’s ' +
        'currency; "client" — a price the studio agreed with this client ' +
        '(widgets_set_client_price), which replaces the card for every widget of this kind ' +
        'the client has.'),
    live_since: DateOnlySchema.describe('The day it started serving.'),
    key_prefix: z
        .string()
        .describe('The public half of its API key. The secret is shown once and never again.'),
    key_rotated_at: Rfc3339Schema.nullable().describe('When the key was last rotated, or null.'),
    previous_key_expires_at: Rfc3339Schema.nullable().describe('When the key it replaced stops working — 24 hours after the rotation — or null.'),
    installed_via_work_order: z
        .string()
        .nullable()
        .describe('The work order that installed it, by reference, or null.'),
    hosted_by: HostedSurfaceSchema.nullable().describe('The portal module that hosts this widget — "blog" or "forms" — snapshotted from its ' +
        'type when it was created, or null for a widget that runs as its own service and ' +
        'meters through the usage API.'),
    publishable_key: z
        .string()
        .nullable()
        .describe('The publishable key, fes_pk_…, for a widget the portal hosts; null for every other ' +
        'widget. Public by design and readable at any time, like a payment provider’s ' +
        'publishable key: it reads published content through the delivery API and opens ' +
        'nothing else. The secret key beside it (key_prefix) is the server-side one.'),
    ...CONFIG_FIELDS,
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('One widget on one client’s site, and what a use of it costs.');
/* ------------------------------------------------------- client prices (46d) */
/**
 * The four numbers a widget is priced by, in one client's billing currency — what a
 * client price sets and what the rate card would give instead (session 46d).
 */
export const WidgetPriceTermsSchema = z
    .object({
    unit_amount: MoneySchema.describe('Per billable unit. Zero for a widget sold by the month.'),
    monthly_amount: MoneySchema.describe('Per full calendar month, billed in arrears on the 1st. Zero for none.'),
    included_qty: z.int().describe('Units included each month. 0 when unit_amount is zero.'),
    monthly_cap: z.int().describe('The monthly ceiling on units. 0 when unit_amount is zero.'),
})
    .describe('A widget price: per unit, per month, the allowance and the cap.');
/** One change to a client's price for a kind of widget, as it was recorded. */
export const ClientPriceEntrySchema = z
    .object({
    price: WidgetPriceTermsSchema.nullable().describe('What the client pays from then on, or null for "back to the rate card".'),
    reason: z.string().describe('Why — as the studio wrote it when it set the price.'),
    set_at: Rfc3339Schema,
    set_by: z
        .string()
        .nullable()
        .describe('The studio person who set it, by name, or null if the account is gone.'),
})
    .describe('One entry in a client’s price history. Appended, never edited.');
/**
 * One kind of widget on one client's price list: what a widget of it costs this client
 * now, what the rate card would charge instead, and how it got there.
 */
export const ClientWidgetPriceSchema = z
    .object({
    widget_type: CatalogCodeSchema.describe('The kind of widget, by catalog code.'),
    widget_type_name: z.string().describe('The kind’s name — "Blog".'),
    source: z
        .enum(WIDGET_PRICE_SOURCES)
        .describe('"client" while a price the studio agreed is in force; "card" otherwise.'),
    current: WidgetPriceTermsSchema.describe('What this client pays for a widget of this kind now, and what a new one starts at.'),
    card: WidgetPriceTermsSchema.describe('What the rate card would charge this client today — in its currency, with the ' +
        'allowance its retainer gives — for reference beside a price of its own.'),
    history: z
        .array(ClientPriceEntrySchema)
        .describe('Every change to this client’s price for the kind, newest first.'),
})
    .describe('A client’s price for one kind of widget.');
export const ClientPriceListSchema = z
    .object({
    client: ClientSlugSchema,
    items: z
        .array(ClientWidgetPriceSchema)
        .describe('Every kind of widget the client has one of or has had a price for, by catalog code.'),
})
    .describe('A client’s own prices, beside the rate card’s.');
/** What setting or clearing a client price answers with. */
export const ClientPriceChangeSchema = z
    .object({
    price: ClientWidgetPriceSchema,
    widgets: z
        .array(WidgetSchema)
        .describe('Every widget of this kind the client has, at the price now in force.'),
})
    .describe('The client’s price for the kind after the change, and the widgets it re-priced.');
/**
 * What the widget's key reads from `GET /api/v1/usage/config`.
 *
 * `widget_type` is on the body on purpose. All four placer codes share `plac` in the
 * reference (`refs.ts` `widgetRefTypeOf` takes four letters), so a service cannot read
 * a widget's family off its ref — and hand-typing the family beside the key at install
 * time is a typo that runs a rug through the furniture pipeline. The type's code is
 * the fact, and this is where the service learns it
 * (`.plans/ai-product-placer/plan/evidence/plan-review.md` §Minors).
 *
 * It carries no key, no client and no money: what a key holder learns here is the
 * origins, the categories and the allowances of the site it is already running on.
 */
export const WidgetConfigSchema = z
    .object({
    widget: WidgetRefSchema,
    widget_type: CatalogCodeSchema.describe('The kind of widget, by catalog code. The service reads its family from this rather ' +
        'than from the reference, because several kinds can share a reference segment.'),
    ...CONFIG_FIELDS,
})
    .describe('One widget’s configuration, as the widget’s own key reads it.');
/**
 * What a client's own `PATCH …/widgets/:ref/config` takes: the keys being changed.
 *
 * **A body shape and deliberately not a validator.** The thing that decides whether a
 * config is legal is the widget's **snapshotted `config_schema`**, turned into zod by
 * `schemas/widgetConfig.ts`'s `configSchemaToZod` — one validator, per widget, from
 * the row (`.plans/ai-product-placer/plan/evidence/plan-review.md` D9). A zod object
 * written here would be a second opinion about the same question, and it would be the
 * wrong one the first time a widget type gained a property.
 *
 * So this says the one thing a route can honestly say before it has read the row: the
 * body is a JSON object. `usage.setConfigByClient` does the rest — the `x-editor:
 * client` filter first, so a key is named, then the snapshot's own validator.
 *
 * It is a **`PATCH`**, so the body is the changes and never the whole config: a
 * client's form shows the handful of properties the schema marks as theirs, and a
 * body that had to carry `origins` to change `brand_mark` would be a form that could
 * silently rewrite a key it does not display.
 */
export const WidgetConfigChangesSchema = z
    .record(z.string(), z.unknown())
    .describe('The settings being changed, by key. Only the keys the widget type’s schema marks ' +
    '"x-editor": "client" may be named; everything else is the studio’s. The values are ' +
    'validated against the widget’s own snapshotted config schema, so what is legal here ' +
    'is what that schema says.');
/** A widget and its key, answered exactly once — at creation and at rotation. */
export const WidgetWithKeySchema = z
    .object({
    widget: WidgetSchema,
    api_key: z
        .string()
        .describe('The key, fes_wk_<prefix>_<secret>. Shown once and stored only as a hash — there ' +
        'is no way to read it again, only to rotate it. Give it to the widget service ' +
        'as an environment variable; it never reaches a browser.'),
})
    .describe('A widget and the key it is metered with. The key is not recoverable.');
/**
 * Which of a widget's two keys a rotation replaces (session 46).
 *
 * Every widget has the **secret** key, `fes_wk_…`, and only a portal-hosted one has a
 * **publishable** key beside it, `fes_pk_…`. They rotate differently: the secret key
 * keeps the old one working for 24 hours, and the publishable key is replaced at once,
 * because a public key protects nothing a grace period would protect.
 */
export const WIDGET_KEY_KINDS = ['secret', 'publishable'];
export const WidgetKeyKindSchema = z
    .enum(WIDGET_KEY_KINDS)
    .describe('Which key: "secret" — the server-side fes_wk_… key every widget has; "publishable" — ' +
    'the public fes_pk_… key a portal-hosted widget (a blog, forms) also has.');
/** What a rotation answers: the widget, which key moved, and the new secret if it was that one. */
export const WidgetKeyRotationSchema = z
    .object({
    widget: WidgetSchema,
    key: WidgetKeyKindSchema.describe('The key that was replaced.'),
    api_key: z
        .string()
        .nullable()
        .describe('The new secret key, fes_wk_<prefix>_<secret>, when the secret key was rotated — shown ' +
        'once and stored only as a hash. Null when the publishable key was rotated: the new ' +
        'one is widget.publishable_key, and the secret key did not change.'),
})
    .describe('A widget after one of its keys was replaced.');
/* -------------------------------------------------------------- the numbers */
export const WidgetSummarySchema = z
    .object({
    widget: WidgetRefSchema,
    name: z.string().describe('What the client calls it.'),
    widget_type: CatalogCodeSchema,
    unit_noun: z.string().describe('One billable event, in the client’s words.'),
    period: PeriodSchema,
    status: WidgetStatusSchema,
    state: UsageStateNameSchema.describe('What it would answer a call right now.'),
    mode: BillingModeSchema,
    used: z.int().describe('Every unit recorded this period.'),
    billable: z.int().describe('Units past the allowance and inside the cap.'),
    included: z.int().describe('Units included each month before anything is billed.'),
    cap: z.int().describe('The monthly ceiling on units. Never below included.'),
    unit_amount: MoneySchema.describe('What one unit costs, in the client’s billing currency. Zero when it is not charged per ' +
        'unit.'),
    monthly_amount: MoneySchema.describe('What it costs each month whatever it does, billed in arrears for each full month it ' +
        'was live. Not part of amount, which is usage only.'),
    amount: MoneySchema.describe('billable × unit_amount — "Cost so far" on the widget page.'),
    projected_qty: z
        .int()
        .describe('Units by the end of the month at this rate, to the nearest ten — the page’s ' +
        '"≈ 1,630". Equal to used once the month is over.'),
    projected_amount: MoneySchema.describe('What the projection would cost — the page’s "≈ $75.60".'),
    days_elapsed: z.int().describe('Days of the period counted so far, including today.'),
    days_in_period: z.int().describe('Days in the whole month.'),
    worst_case: MoneySchema.describe('(cap − included) × unit_amount. The most it can bill.'),
    last_period: z
        .object({
        period: PeriodSchema.describe('The month before this one, YYYY-MM.'),
        used: z.int().describe('Every unit recorded that month.'),
        billable: z.int().describe('Units past the allowance and inside the cap.'),
        amount: MoneySchema.describe('billable × unit_amount — what that month was billed.'),
        closed: z.boolean().describe('True once the month became an invoice line.'),
    })
        .nullable()
        .describe('The month before, from usage_month — "July’s conversations were billed at $21.60". ' +
        'Null when there is no month before this one.'),
})
    .describe('What one widget has done this month, and what it is on course to do.');
export const UsageSummarySchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema,
    mode: BillingModeSchema,
    widgets: z
        .array(WidgetSummarySchema)
        .describe('One row per widget, in the order they went live.'),
    amount: MoneySchema.describe('What every widget comes to this period — billing.html’s $67.28.'),
    balance: MoneySchema.nullable().describe('The prepaid balance, or null for a postpay client.'),
})
    .describe('Every widget a client has, and what this month has come to.');
export const UsageDailyPointSchema = z
    .object({
    on: DateOnlySchema.describe('The day, YYYY-MM-DD.'),
    qty: z.int().describe('Units recorded that day.'),
})
    .describe('One day of a widget’s usage. The bars on the widget page’s chart.');
export const UsageSeriesSchema = z
    .object({
    widget: WidgetRefSchema,
    period: PeriodSchema,
    unit_noun: z.string(),
    days: z.array(UsageDailyPointSchema).describe('Every day of the period so far, in order.'),
    total: z.int().describe('The sum of the days — the chart’s caption.'),
})
    .describe('A widget’s usage day by day, for the chart on its page.');
export const UsageEventSchema = z
    .object({
    event_key: z.string().describe('The key the widget generated.'),
    widget: WidgetRefSchema,
    occurred_at: Rfc3339Schema.describe('When the widget says it happened.'),
    recorded_at: Rfc3339Schema.describe('When the portal wrote it.'),
    period: PeriodSchema.describe('The month it counts against.'),
    qty: z.int().describe('How many units this one event is. Almost always 1.'),
    unit_amount: MoneySchema.describe('What a unit cost at the moment it was recorded.'),
    state: UsageStateNameSchema.describe('What the widget was told when this was recorded.'),
    billed: z
        .boolean()
        .describe('True once it is on an invoice line or has been debited from the balance.'),
    meta: z.record(z.string(), z.unknown()).describe('Whatever the widget sent with it.'),
})
    .describe('One metered event, exactly as it was recorded.');
/* -------------------------------------------------------------- the ledger */
export const CreditLedgerEntrySchema = z
    .object({
    client: ClientSlugSchema,
    kind: CreditLedgerKindSchema,
    delta: MoneySchema.describe('Signed: positive for money in, negative for money spent.'),
    balance_after: MoneySchema.describe('The balance once this row applied. Never negative.'),
    ref: z
        .string()
        .describe('What caused it — a usage event key, a payment id, or a staff note. Free text, ' +
        'never parsed.'),
    note: z.string().nullable().describe('Why, for an adjustment. Null for everything else.'),
    created_at: Rfc3339Schema,
})
    .describe('One row of the prepaid ledger. Append-only.');
export const CreditBalanceSchema = z
    .object({
    client: ClientSlugSchema,
    balance: MoneySchema.describe('What is left. Zero when there has never been a top-up.'),
    mode: BillingModeSchema.describe('A postpay client can still hold a balance — it is spent first, and usage moves to ' +
        'the invoice once it reaches zero (SERVICES.md §Switching modes).'),
    auto_top_up: z
        .object({
        enabled: z
            .boolean()
            .describe('False pauses it without forgetting the threshold and the amount.'),
        threshold_amount: MoneySchema.describe('Below this, the card on file is charged.'),
        topup_amount: MoneySchema.describe('How much is added when it is.'),
        last_attempt_at: Rfc3339Schema.nullable(),
        last_attempt_status: z
            .string()
            .nullable()
            .describe('"succeeded", or the failure code the gateway gave.'),
    })
        .nullable()
        .describe('The standing instruction, or null when the client has never set one.'),
})
    .describe('A client’s prepaid balance and how it is topped up.');
export const SetAutoTopUpSchema = z
    .strictObject({
    enabled: z.boolean().describe('False pauses it without forgetting the numbers.'),
    threshold_amount: MoneySchema.describe('Charge the card when the balance falls below this. The default is $10 (setting ' +
        'usage.auto_top_up_threshold).'),
    topup_amount: MoneySchema.describe('How much to add. At least the minimum top-up (setting usage.minimum_top_up, $25), ' +
        'so card fees stay proportionate.'),
})
    .describe('The standing top-up instruction for a prepaid client.');
/* ------------------------------------------------------- what the pages need */
/**
 * Everything `client/widget-chatbot.html` and `client/widget-booking.html` print, in
 * one answer. Session 19 renders it; nothing else composes these three.
 */
export const WidgetPageSchema = z
    .object({
    widget: WidgetSchema,
    summary: WidgetSummarySchema,
    series: UsageSeriesSchema,
})
    .describe('One widget’s page: what it is, what it has done, and the chart.');
//# sourceMappingURL=usage.js.map